import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Comment } from 'services/comments/types';
import type { Event } from 'services/events/types';
import { apiObjectives } from 'services/objectives/api';
import { apiTasks } from 'services/tasks/api';
import { getApiBaseUrl } from 'utils/user';
import { Project } from './types';

type ProjectUpdate = Partial<Project> & Pick<Project, 'id'> & {
  archiveExclusiveRelated?: boolean;
};

export const apiProjects = createApi({
  reducerPath: 'projectsApi',
  tagTypes: ['Project', 'Comment', 'Event'],
  baseQuery: fetchBaseQuery({
    baseUrl: `${getApiBaseUrl()}/`,
    credentials: 'include',
    prepareHeaders: (headers) => {
      headers.set('X-Client', 'web');
      return headers;
    },
  }),
  endpoints: builder => ({
    getProjects: builder.query<Project[], void>({
      query: () => 'projects',
      transformResponse: (data: { projects: Project[] }) => data.projects,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Project'],
    }),
    getProject: builder.query<Project, string>({
      query: id => `projects/${id}`,
      transformResponse: (data: { project: Project }) => data.project,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: ['Project'],
    }),
    getProjectComments: builder.query<Comment[], string>({
      query: id => `projects/${id}/comments`,
      transformResponse: (data: { comments: Comment[] }) => data.comments,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Comment', id: `project-${id}` }],
    }),
    addProjectComment: builder.mutation<Comment, { comment: string; projectId: string }>({
      query: ({ comment, projectId }) => ({
        url: `projects/${projectId}/comments`,
        method: 'POST',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Comment', id: `project-${projectId}` }],
    }),
    patchProjectComment: builder.mutation<Comment, { comment: string; id: string; projectId: string }>({
      query: ({ comment, id, projectId }) => ({
        url: `projects/${projectId}/comments/${id}`,
        method: 'PATCH',
        body: { comment },
      }),
      transformResponse: (data: { comment: Comment }) => data.comment,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Comment', id: `project-${projectId}` }],
    }),
    deleteProjectComment: builder.mutation<string, { id: string; projectId: string }>({
      query: ({ id, projectId }) => ({
        url: `projects/${projectId}/comments/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { commentId: string }) => data.commentId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'Comment', id: `project-${projectId}` }],
    }),
    getProjectEvents: builder.query<Event[], string>({
      query: id => `projects/${id}/events`,
      transformResponse: (data: { events: Event[] }) => data.events,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      providesTags: (_result, _error, id) => [{ type: 'Event', id }],
    }),
    addProject: builder.mutation<Project, Partial<Project>>({
      query: body => ({
        url: `projects`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: { project: Project }) => data.project,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: ['Project'],
    }),
    patchManyProjects: builder.mutation<Project[], {
      ids: string[];
      infos: {
        assignee?: string | null;
      };
    }>({
      query: ({ ids, infos }) => ({
        url: `projects/batch`,
        method: 'PATCH',
        body: {
          ids,
          infos,
        },
      }),
      transformResponse: (data: { projects: Project[] }) => data.projects,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { ids }) => [
        'Project',
        ...ids.map(id => ({ type: 'Event' as const, id })),
      ],
    }),
    patchProject: builder.mutation<Project, ProjectUpdate>({
      query: ({ id, ...body }) => ({
        url: `projects/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (data: { project: Project }) => data.project,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { id }) => ['Project', { type: 'Event', id }],
      async onQueryStarted({ archiveExclusiveRelated }, { dispatch, queryFulfilled }) {
        if (!archiveExclusiveRelated) {
          return;
        }
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(apiTasks.util.invalidateTags(['Task']));
        dispatch(apiObjectives.util.invalidateTags(['Objective']));
      },
    }),
    updateProject: builder.mutation<Project, ProjectUpdate>({
      query: ({ id, ...body }) => ({
        url: `projects/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (data: { project: Project }) => data.project,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (_result, _error, { id }) => ['Project', { type: 'Event', id }],
      async onQueryStarted({ archiveExclusiveRelated }, { dispatch, queryFulfilled }) {
        if (!archiveExclusiveRelated) {
          return;
        }
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(apiTasks.util.invalidateTags(['Task']));
        dispatch(apiObjectives.util.invalidateTags(['Objective']));
      },
    }),
    deleteProject: builder.mutation<string, string>({
      query: id => ({
        url: `projects/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (data: { projectId: string }) => data.projectId,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: ['Project'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useLazyGetProjectQuery,
  useGetProjectCommentsQuery,
  useGetProjectEventsQuery,
  useAddProjectCommentMutation,
  usePatchProjectCommentMutation,
  useDeleteProjectCommentMutation,
  useAddProjectMutation,
  usePatchManyProjectsMutation,
  usePatchProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
} = apiProjects;
